// src/pages/admin/MatricClasses.js

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  ListItemText
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import PasswordRoundedIcon from '@mui/icons-material/PasswordRounded';
import Chip from '@mui/material/Chip';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import UserProfileDialog from '../components/UserProfileDialog';
import api from '../services/api';
import {
  fetchUsers,
  registerUser,
  deleteUser,
  updateUser,
  setUserSubjects,
  lockUserAccount,
  unlockUserAccount,
  changeUserPassword
} from '../services/user';
import { fetchSubjects } from '../services/subject';

const matricSubjectNames = [
  'Mathematics',
  'Mathematical Literacy',
  'Physical Sciences',
  'Life Sciences',
  'Accounting',
  'Business Studies',
  'Economics',
  'History',
  'Geography',
  'IsiZulu FAL',
  'English Home Language',
  'English FAL',
  'Afrikaans FAL'
];
const curriculumOptions = ['CAPS', 'IEB', 'CAMBRIDGE', 'PEARSON EDEXCEL'];

const defaultEducatorForm = {
  name: '',
  surname: '',
  email: '',
  password: '',
  phone: '',
  country: '',
  province: '',
  town: '',
  suburb: '',
  curricula: [],
  tutoringOptions: [],
  subjectIds: []
};
const defaultLearnerForm = {
  name: '',
  surname: '',
  email: '',
  password: '',
  phone: '',
  country: '',
  province: '',
  town: '',
  suburb: '',
  curricula: [],
  gradeLevel: '12',
  tutorIds: [],
  tutoringOptions: [],
  subjectIds: []
};
const defaultClassForm = {
  subject: '',
  gradeLevel: '12',
  startTime: '',
  endTime: '',
  scheduleLabel: '',
  tutorId: '',
  studentIds: []
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const formatLabelList = (value, fallback = '--') => {
  const list = Array.isArray(value) ? value.filter(Boolean) : [];
  if (!list.length) return fallback;
  return list
    .map((entry) =>
      String(entry)
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    )
    .join(', ');
};

const formatDeliveryMode = (user) => {
  if (user?.matricEnrollment?.deliveryMode) return formatLabelList([user.matricEnrollment.deliveryMode]);
  const direct = Array.isArray(user?.tutoringOptions) ? user.tutoringOptions : [];
  if (direct.length) return formatLabelList(direct);
  const fallback = user?.mode ? [user.mode] : [];
  return formatLabelList(fallback);
};

const formatLocationSummary = (user) => {
  const parts = [user?.suburb, user?.town, user?.province, user?.country]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : '--';
};

const formatProfileSummary = (user) => {
  const parts = [];
  if (user?.phone) parts.push(`Phone: ${user.phone}`);
  if (Array.isArray(user?.curricula) && user.curricula.length) parts.push(`Curricula: ${user.curricula.join(', ')}`);
  if (Array.isArray(user?.gradeLevels) && user.gradeLevels.length) parts.push(`Grades: ${user.gradeLevels.join(', ')}`);
  if (Array.isArray(user?.subjectFocus) && user.subjectFocus.length) parts.push(`Focus: ${user.subjectFocus.join(', ')}`);
  return parts.length ? parts.join(' | ') : '--';
};

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizeISO = (localValue) => {
  if (!localValue) return null;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const StatusChip = ({ locked }) => (
  <Chip
    size="small"
    label={locked ? 'Locked' : 'Active'}
    color={locked ? 'warning' : 'success'}
    variant={locked ? 'outlined' : 'filled'}
  />
);

const MatricClasses = () => {
  const [activeTab, setActiveTab] = useState('classes');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [search, setSearch] = useState('');

  const [educators, setEducators] = useState([]);
  const [learners, setLearners] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [educatorDialog, setEducatorDialog] = useState({ open: false, loading: false, mode: 'create', data: null });
  const [learnerDialog, setLearnerDialog] = useState({ open: false, loading: false, mode: 'create', data: null });
  const [classDialog, setClassDialog] = useState({ open: false, loading: false, mode: 'create', data: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, loading: false, title: '', message: '', action: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [viewDialog, setViewDialog] = useState({ open: false, user: null });
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    user: null,
    loading: false,
    newPassword: '',
    confirmPassword: ''
  });

  const [educatorForm, setEducatorForm] = useState(defaultEducatorForm);
  const [learnerForm, setLearnerForm] = useState(defaultLearnerForm);
  const [classForm, setClassForm] = useState(defaultClassForm);

  const educatorMap = useMemo(() => {
    const map = new Map();
    educators.forEach((item) => {
      if (item?._id) map.set(String(item._id), item);
    });
    return map;
  }, [educators]);

  const learnerMap = useMemo(() => {
    const map = new Map();
    learners.forEach((item) => {
      if (item?._id) map.set(String(item._id), item);
    });
    return map;
  }, [learners]);

  const subjectMap = useMemo(() => {
    const map = new Map();
    subjects.forEach((item) => {
      if (item?._id) map.set(String(item._id), item);
    });
    return map;
  }, [subjects]);

  const getDisplayName = useCallback(
    (user) => `${user?.name || ''} ${user?.surname || ''}`.trim() || user?.email || 'User',
    [],
  );

  const matricSubjects = useMemo(() => {
    const byName = new Map(
      subjects
        .filter((subject) => subject?.name)
        .map((subject) => [subject.name.toLowerCase(), subject])
    );
    return matricSubjectNames
      .map((name) => byName.get(name.toLowerCase()))
      .filter(Boolean);
  }, [subjects]);

  const classSubjectOptions = useMemo(() => {
    const options = matricSubjects.length
      ? matricSubjects.map((subject) => subject.name)
      : [...matricSubjectNames];
    if (classForm.subject && !options.includes(classForm.subject)) {
      options.push(classForm.subject);
    }
    return options;
  }, [matricSubjects, classForm.subject]);

  const normalizeSubjectIds = useCallback((user) => {
    const raw = Array.isArray(user?.subjectIds)
      ? user.subjectIds
      : Array.isArray(user?.subjects)
        ? user.subjects
        : [];
    return raw
      .map((entry) => (typeof entry === 'object' && entry !== null ? entry._id : entry))
      .filter(Boolean)
      .map((id) => String(id));
  }, []);

  const getSubjectNames = useCallback(
    (user) => {
      if (user?.matricEnrollment?.selectedSubjects?.length) {
        return user.matricEnrollment.selectedSubjects.join(', ');
      }
      const raw = Array.isArray(user?.subjectIds)
        ? user.subjectIds
        : Array.isArray(user?.subjects)
          ? user.subjects
          : [];
      if (!raw.length) return '?';
      const names = raw
        .map((entry) => {
          if (typeof entry === 'object' && entry !== null) {
            if (entry.name) return entry.name;
            if (entry._id && subjectMap.has(String(entry._id))) {
              return subjectMap.get(String(entry._id)).name;
            }
            return null;
          }
          const subject = subjectMap.get(String(entry));
          return subject ? subject.name : null;
        })
        .filter(Boolean);
      return names.length ? names.join(', ') : '?';
    },
    [subjectMap]
  );

  const loadSubjects = useCallback(async () => {
    const response = await fetchSubjects({ active: true, limit: 500 });
    const items = Array.isArray(response?.items) ? response.items : [];
    setSubjects(items);
  }, []);

  const loadEducators = useCallback(async () => {
    const list = await fetchUsers({ role: 'tutor', portal: 'matric' });
    setEducators(Array.isArray(list) ? list : []);
  }, []);

  const loadLearners = useCallback(async () => {
    const [list, summaryResponse] = await Promise.all([
      fetchUsers({ role: 'student', program: 'matric' }),
      api.get('/matric-rewrite/admin/learner-summaries').catch(() => null),
    ]);
    const summaries = new Map((summaryResponse?.data?.items || []).map((item) => [String(item.lmsStudentId), item]));
    setLearners((Array.isArray(list) ? list : []).map((learner) => ({
      ...learner,
      matricEnrollment: summaries.get(String(learner._id)) || null,
    })));
    if (!summaryResponse) setFeedback({ type: 'error', message: 'Matric intake details could not be loaded. Showing LMS profile details only.' });
  }, []);

  const loadClasses = useCallback(async () => {
    const response = await api.get('/session', { params: { program: 'matric' } });
    const list = Array.isArray(response.data) ? response.data : [];
    setClasses(list);
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadSubjects(), loadEducators(), loadLearners(), loadClasses()]);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load matric programme data.');
    } finally {
      setLoading(false);
    }
  }, [loadClasses, loadEducators, loadLearners, loadSubjects]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);
  const filteredEducators = useMemo(() => {
    if (!search.trim()) return educators;
    const lowered = search.trim().toLowerCase();
    return educators.filter((item) => {
      const name = `${item.name || ''} ${item.surname || ''}`.trim().toLowerCase();
      const email = (item.email || '').toLowerCase();
      return name.includes(lowered) || email.includes(lowered);
    });
  }, [educators, search]);

  const filteredLearners = useMemo(() => {
    if (!search.trim()) return learners;
    const lowered = search.trim().toLowerCase();
    return learners.filter((item) => {
      const name = `${item.name || ''} ${item.surname || ''}`.trim().toLowerCase();
      const email = (item.email || '').toLowerCase();
      const intake = item.matricEnrollment;
      return [name, email, intake?.applicationReference, intake?.intakeLabel,
        intake?.deliveryMode, intake?.curriculum, ...(intake?.selectedSubjects || [])]
        .some((value) => String(value || '').toLowerCase().includes(lowered));
    });
  }, [learners, search]);

  const filteredClasses = useMemo(() => {
    if (!search.trim()) return classes;
    const lowered = search.trim().toLowerCase();
    return classes.filter((cls) => {
      const subject = (cls.subject || '').toLowerCase();
      const tutor = educatorMap.get(String(cls.tutorId));
      const tutorName = tutor ? `${tutor.name || ''} ${tutor.surname || ''}`.trim().toLowerCase() : '';
      return subject.includes(lowered) || tutorName.includes(lowered);
    });
  }, [classes, search, educatorMap]);

  const resetEducatorForm = () => setEducatorForm(defaultEducatorForm);
  const resetLearnerForm = () => setLearnerForm(defaultLearnerForm);
  const resetClassForm = () => setClassForm(defaultClassForm);

  const openEducatorDialog = (educator = null) => {
    if (educator) {
      const options = Array.isArray(educator.tutoringOptions) ? educator.tutoringOptions : [];
      setEducatorForm({
        name: educator.name || '',
        surname: educator.surname || '',
        email: educator.email || '',
        password: '',
        phone: educator.phone || '',
        country: educator.country || '',
        province: educator.province || '',
        town: educator.town || '',
        suburb: educator.suburb || '',
        curricula: Array.isArray(educator.curricula) ? educator.curricula : [],
        tutoringOptions: options,
        subjectIds: normalizeSubjectIds(educator)
      });
      setEducatorDialog({ open: true, loading: false, mode: 'edit', data: educator });
      return;
    }
    resetEducatorForm();
    setEducatorDialog({ open: true, loading: false, mode: 'create', data: null });
  };

  const openLearnerDialog = (learner = null) => {
    if (learner) {
      const options = Array.isArray(learner.tutoringOptions) ? learner.tutoringOptions : [];
      setLearnerForm({
        name: learner.name || '',
        surname: learner.surname || '',
        email: learner.email || '',
        password: '',
        phone: learner.phone || '',
        country: learner.country || '',
        province: learner.province || '',
        town: learner.town || '',
        suburb: learner.suburb || '',
        curricula: Array.isArray(learner.curricula) ? learner.curricula : [],
        gradeLevel: String(learner.grade || learner.gradeLevel || '12'),
        tutorIds: Array.isArray(learner.tutorIds) ? learner.tutorIds.map((id) => String(id?._id || id)) : [],
        tutoringOptions: options,
        subjectIds: normalizeSubjectIds(learner)
      });
      setLearnerDialog({ open: true, loading: false, mode: 'edit', data: learner });
      return;
    }
    resetLearnerForm();
    setLearnerDialog({ open: true, loading: false, mode: 'create', data: null });
  };

  const openClassDialog = (mode, cls = null) => {
    if (mode === 'edit' && cls) {
      setClassForm({
        subject: cls.subject || '',
        gradeLevel: cls.gradeLevel != null ? String(cls.gradeLevel) : '12',
        startTime: toDateTimeLocalValue(cls.startTime),
        endTime: toDateTimeLocalValue(cls.endTime),
        scheduleLabel: cls.scheduleLabel || '',
        tutorId: cls.tutorId ? String(cls.tutorId) : '',
        studentIds: Array.isArray(cls.studentIds) ? cls.studentIds.map(String) : []
      });
      setClassDialog({ open: true, loading: false, mode: 'edit', data: cls });
    } else {
      resetClassForm();
      setClassDialog({ open: true, loading: false, mode: 'create', data: null });
    }
  };

  const closeEducatorDialog = () => setEducatorDialog({ open: false, loading: false, mode: 'create', data: null });
  const closeLearnerDialog = () => setLearnerDialog({ open: false, loading: false, mode: 'create', data: null });
  const closeClassDialog = () => setClassDialog({ open: false, loading: false, mode: 'create', data: null });
  const handleEducatorSubmit = async (event) => {
    event.preventDefault();
    const isEdit = educatorDialog.mode === 'edit';
    if (!educatorForm.name || !educatorForm.surname || !educatorForm.email || (!isEdit && !educatorForm.password)) {
      setFeedback({
        type: 'error',
        message: isEdit ? 'Name, surname, and email are required.' : 'All educator fields are required.'
      });
      return;
    }
    if (!Array.isArray(educatorForm.tutoringOptions) || !educatorForm.tutoringOptions.length) {
      setFeedback({ type: 'error', message: 'Select at least one tutoring option for the educator.' });
      return;
    }
    if (!educatorForm.subjectIds.length) {
      setFeedback({ type: 'error', message: 'Select at least one subject for the educator.' });
      return;
    }
    setEducatorDialog((prev) => ({ ...prev, loading: true }));
    const tutoringOptions = educatorForm.tutoringOptions || [];
    try {
      let saved = null;
      if (isEdit && educatorDialog.data?._id) {
        saved = await updateUser(educatorDialog.data._id, {
          name: educatorForm.name,
          surname: educatorForm.surname,
          email: educatorForm.email,
          phone: educatorForm.phone,
          country: educatorForm.country,
          province: educatorForm.province,
          town: educatorForm.town,
          suburb: educatorForm.suburb,
          curricula: educatorForm.curricula,
          role: 'tutor',
          program: ['general', 'matric'],
          activePortal: 'matric',
          tutoringOptions
        });
        setFeedback({ type: 'success', message: 'Educator updated successfully.' });
      } else {
        saved = await registerUser({
          name: educatorForm.name,
          surname: educatorForm.surname,
          email: educatorForm.email,
          password: educatorForm.password,
          phone: educatorForm.phone,
          country: educatorForm.country,
          province: educatorForm.province,
          town: educatorForm.town,
          suburb: educatorForm.suburb,
          curricula: educatorForm.curricula,
          role: 'tutor',
          program: ['general', 'matric'],
          activePortal: 'matric',
          tutoringOptions
        });
        setFeedback({ type: 'success', message: 'Educator added successfully.' });
      }
      if (saved?._id) {
        await setUserSubjects(saved._id, educatorForm.subjectIds);
      }
      closeEducatorDialog();
      await loadEducators();
    } catch (err) {
      setEducatorDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to save educator.' });
    }
  };

  const handleLearnerSubmit = async (event) => {
    event.preventDefault();
    const isEdit = learnerDialog.mode === 'edit';
    if (!learnerForm.name || !learnerForm.surname || !learnerForm.email || (!isEdit && !learnerForm.password)) {
      setFeedback({
        type: 'error',
        message: isEdit ? 'Name, surname, and email are required.' : 'All learner fields are required.'
      });
      return;
    }
    if (!Array.isArray(learnerForm.tutoringOptions) || !learnerForm.tutoringOptions.length) {
      setFeedback({ type: 'error', message: 'Select at least one tutoring option for the learner.' });
      return;
    }
    if (!learnerForm.subjectIds.length) {
      setFeedback({ type: 'error', message: 'Select at least one subject for the learner.' });
      return;
    }
    setLearnerDialog((prev) => ({ ...prev, loading: true }));
    const tutoringOptions = learnerForm.tutoringOptions || [];
    try {
      let saved = null;
      if (isEdit && learnerDialog.data?._id) {
        saved = await updateUser(learnerDialog.data._id, {
          name: learnerForm.name,
          surname: learnerForm.surname,
          email: learnerForm.email,
          phone: learnerForm.phone,
          country: learnerForm.country,
          province: learnerForm.province,
          town: learnerForm.town,
          suburb: learnerForm.suburb,
          curricula: learnerForm.curricula,
          grade: Number(learnerForm.gradeLevel) || 12,
          role: 'student',
          program: 'matric',
          tutorIds: learnerForm.tutorIds,
          tutoringOptions
        });
        setFeedback({ type: 'success', message: 'Learner updated successfully.' });
      } else {
        saved = await registerUser({
          name: learnerForm.name,
          surname: learnerForm.surname,
          email: learnerForm.email,
          password: learnerForm.password,
          phone: learnerForm.phone,
          country: learnerForm.country,
          province: learnerForm.province,
          town: learnerForm.town,
          suburb: learnerForm.suburb,
          curricula: learnerForm.curricula,
          grade: Number(learnerForm.gradeLevel) || 12,
          role: 'student',
          program: 'matric',
          tutorIds: learnerForm.tutorIds,
          tutoringOptions
        });
        setFeedback({ type: 'success', message: 'Learner added successfully.' });
      }
      if (saved?._id) {
        await setUserSubjects(saved._id, learnerForm.subjectIds);
      }
      closeLearnerDialog();
      await loadLearners();
    } catch (err) {
      setLearnerDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to save learner.' });
    }
  };

  const handleClassSave = async (event) => {
    event.preventDefault();
    if (!classForm.subject) {
      setFeedback({ type: 'error', message: 'Subject is required.' });
      return;
    }
    if (!classForm.startTime || !classForm.endTime) {
      setFeedback({ type: 'error', message: 'Start and end time are required.' });
      return;
    }
    if (!classForm.tutorId) {
      setFeedback({ type: 'error', message: 'Select an educator for this class.' });
      return;
    }

    const payload = {
      program: 'matric',
      subject: classForm.subject,
      gradeLevel: Number(classForm.gradeLevel) || 12,
      startTime: normalizeISO(classForm.startTime),
      endTime: normalizeISO(classForm.endTime),
      scheduleLabel: classForm.scheduleLabel || undefined,
      tutorId: classForm.tutorId,
      studentIds: classForm.studentIds
    };

    if (!payload.startTime || !payload.endTime) {
      setFeedback({ type: 'error', message: 'Invalid start or end time.' });
      return;
    }

    setClassDialog((prev) => ({ ...prev, loading: true }));

    try {
      if (classDialog.mode === 'create') {
        await api.post('/session/create', payload);
        setFeedback({ type: 'success', message: 'Class scheduled successfully.' });
      } else if (classDialog.data?._id) {
        const classId = classDialog.data._id;
        const originalStart = toDateTimeLocalValue(classDialog.data.startTime);
        const originalEnd = toDateTimeLocalValue(classDialog.data.endTime);
        const reschedule = classForm.startTime !== originalStart || classForm.endTime !== originalEnd;
        if (reschedule) {
          await api.patch(`/session/${classId}`, {
            startTime: payload.startTime,
            endTime: payload.endTime
          });
        }
        await api.patch(`/session/${classId}/roster`, {
          tutorId: payload.tutorId,
          studentIds: payload.studentIds,
          subject: payload.subject,
          gradeLevel: payload.gradeLevel,
          scheduleLabel: payload.scheduleLabel,
          program: 'matric'
        });
        setFeedback({ type: 'success', message: 'Class updated successfully.' });
      }
      closeClassDialog();
      await loadClasses();
    } catch (err) {
      setClassDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.response?.data?.message || err?.message || 'Failed to save class.' });
    }
  };
  const requestDeleteUser = (userId, label) => {
    if (!userId) return;
    setConfirmDialog({
      open: true,
      loading: false,
      title: 'Remove user',
      message: `Delete ${label}? This action cannot be undone.`,
      action: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await deleteUser(userId);
          setFeedback({ type: 'success', message: 'User removed successfully.' });
          await Promise.all([loadEducators(), loadLearners()]);
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
        } catch (err) {
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
          setFeedback({ type: 'error', message: err?.message || 'Failed to delete user.' });
        }
      }
    });
  };

  const requestDeleteClass = (cls) => {
    if (!cls?._id) return;
    setConfirmDialog({
      open: true,
      loading: false,
      title: 'Delete class',
      message: `Delete ${cls.subject || 'this class'}?`,
      action: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await api.delete(`/session/${cls._id}`);
          setFeedback({ type: 'success', message: 'Class deleted successfully.' });
          await loadClasses();
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
        } catch (err) {
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
          setFeedback({ type: 'error', message: err?.response?.data?.message || err?.message || 'Failed to delete class.' });
        }
      }
    });
  };

  const upsertLearnerLocally = useCallback((updatedPayload) => {
    const updated = updatedPayload?.user || updatedPayload;
    if (!updated?._id) return;
    setLearners((prev) =>
      prev.map((item) => (String(item._id) === String(updated._id) ? { ...item, ...updated } : item))
    );
  }, []);

  const upsertEducatorLocally = useCallback((updatedPayload) => {
    const updated = updatedPayload?.user || updatedPayload;
    if (!updated?._id) return;
    setEducators((prev) =>
      prev.map((item) => (String(item._id) === String(updated._id) ? { ...item, ...updated } : item))
    );
  }, []);

  const handleLockUser = useCallback(
    (user, action, type) => {
      if (!user?._id) return;
      const lockAction = action === 'lock';
      const isEducatorType = type === 'educator';
      const roleLabel = isEducatorType ? 'educator' : 'learner';
      const displayName = `${user.name || ''} ${user.surname || ''}`.trim() || user.email || `this ${roleLabel}`;
      setConfirmDialog({
        open: true,
        loading: false,
        title: lockAction ? `Lock ${roleLabel} account` : `Unlock ${roleLabel} account`,
        message: lockAction
          ? `Lock ${displayName}? They will no longer be able to sign in.`
          : `Unlock ${displayName} so they can sign in again?`,
        action: async () => {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          try {
            const updated = lockAction ? await lockUserAccount(user._id) : await unlockUserAccount(user._id);
            if (isEducatorType) upsertEducatorLocally(updated);
            else upsertLearnerLocally(updated);
            setFeedback({
              type: 'success',
              message: lockAction ? `${isEducatorType ? 'Educator' : 'Learner'} locked successfully.` : `${isEducatorType ? 'Educator' : 'Learner'} unlocked successfully.`
            });
            setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
          } catch (err) {
            setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
            setFeedback({ type: 'error', message: err?.message || 'Failed to update learner lock state.' });
          }
        }
      });
    },
    [upsertEducatorLocally, upsertLearnerLocally]
  );

  const openResetPasswordDialog = useCallback((user) => {
    if (!user?._id) return;
    setPasswordDialog({ open: true, user, loading: false, newPassword: '', confirmPassword: '' });
  }, []);

  const handleResetPassword = useCallback(async () => {
    if (!passwordDialog.user?._id) return;
    if (!passwordDialog.newPassword || passwordDialog.newPassword.length < 8) {
      setFeedback({ type: 'error', message: 'New password must be at least 8 characters.' });
      return;
    }
    if (passwordDialog.newPassword !== passwordDialog.confirmPassword) {
      setFeedback({ type: 'error', message: 'Passwords do not match.' });
      return;
    }
    setPasswordDialog((prev) => ({ ...prev, loading: true }));
    try {
      await changeUserPassword({ userId: passwordDialog.user._id, newPassword: passwordDialog.newPassword });
      setFeedback({ type: 'success', message: 'Password reset successfully.' });
      setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to reset password.' });
    }
  }, [passwordDialog]);

  const handleMenuOpen = (event, type, data) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow({ type, data });
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };
  const classColumns = useMemo(
    () => [
      {
        field: 'subject',
        headerName: 'Class',
        flex: 1.2,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body1" fontWeight={600}>
              {row.subject || 'Class'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Grade {row.gradeLevel ?? '12'}
            </Typography>
            {row.scheduleLabel && (
              <Typography variant="body2" color="text.secondary">{row.scheduleLabel}</Typography>
            )}
          </Stack>
        )
      },
      {
        field: 'tutor',
        headerName: 'Educator',
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => {
          const tutor = educatorMap.get(String(row.tutorId));
          return tutor ? `${tutor.name || ''} ${tutor.surname || ''}`.trim() : '—';
        }
      },
      {
        field: 'schedule',
        headerName: 'Schedule',
        flex: 1.4,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">{formatDateTime(row.startTime)}</Typography>
            <Typography variant="body2" color="text.secondary">{formatDateTime(row.endTime)}</Typography>
          </Stack>
        )
      },
      {
        field: 'learners',
        headerName: 'Learners',
        width: 160,
        renderCell: ({ row }) => {
          const learnerNames = Array.isArray(row.studentIds)
            ? row.studentIds
                .map((id) => learnerMap.get(String(id)))
                .filter(Boolean)
                .map((student) => `${student.name || ''} ${student.surname || ''}`.trim())
            : [];
          const label = learnerNames.length ? `${learnerNames.length} enrolled` : 'None';
          return learnerNames.length ? (
            <Tooltip title={learnerNames.join(', ')}>
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          );
        }
      },
      {
        field: 'actions',
        headerName: '',
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <IconButton size="small" onClick={(event) => handleMenuOpen(event, 'class', row)}>
            <MoreVertRoundedIcon fontSize="small" />
          </IconButton>
        )
      }
    ],
    [educatorMap, learnerMap]
  );

  const educatorColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Educator',
        flex: 1.4,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body1" fontWeight={600}>
              {`${row.name || ''} ${row.surname || ''}`.trim() || 'Unnamed'}
            </Typography>
            <Typography variant="body2" color="text.secondary">{row.email || '--'}</Typography>
          </Stack>
        )
      },
      {
        field: 'mode',
        headerName: 'Delivery mode',
        width: 150,
        renderCell: ({ row }) => formatDeliveryMode(row)
      },
      {
        field: 'phone',
        headerName: 'Phone',
        width: 150,
        renderCell: ({ row }) => row?.phone || '--'
      },
      {
        field: 'location',
        headerName: 'Location',
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }) => formatLocationSummary(row)
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => <StatusChip locked={Boolean(row.isLocked)} />
      },
      {
        field: 'curricula',
        headerName: 'Curricula',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => formatLabelList(row?.curricula)
      },
      {
        field: 'subjects',
        headerName: 'Subjects',
        flex: 1.2,
        minWidth: 220,
        renderCell: ({ row }) => getSubjectNames(row)
      },
      {
        field: 'profileSummary',
        headerName: 'Recorded Details',
        flex: 1.4,
        minWidth: 260,
        renderCell: ({ row }) => {
          const summary = formatProfileSummary(row);
          return summary === '--' ? (
            '--'
          ) : (
            <Tooltip title={summary}>
              <span>{summary}</span>
            </Tooltip>
          );
        }
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => formatDateTime(row?.createdAt)
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            <IconButton size="small" aria-label="View profile" onClick={() => setViewDialog({ open: true, user: row })}>
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="More actions" onClick={(event) => handleMenuOpen(event, 'educator', row)}>
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        )
      }
    ],
    [getSubjectNames]
  );

  const getEducatorNames = useCallback(
    (learner) => {
      if (!Array.isArray(learner?.tutorIds) || !learner.tutorIds.length) return '—';
      return learner.tutorIds
        .map((id) => {
          const tutor = typeof id === 'object' && id !== null ? id : educatorMap.get(String(id));
          if (!tutor) return null;
          return `${tutor.name || ''} ${tutor.surname || ''}`.trim();
        })
        .filter(Boolean)
        .join(', ');
    },
    [educatorMap]
  );

  const learnerColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Learner',
        flex: 1.4,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body1" fontWeight={600}>
              {`${row.name || ''} ${row.surname || ''}`.trim() || 'Unnamed'}
            </Typography>
            <Typography variant="body2" color="text.secondary">{row.email || '—'}</Typography>
          </Stack>
        )
      },
      {
        field: 'grade',
        headerName: 'Grade',
        width: 100,
        renderCell: ({ row }) => row?.grade ?? row?.gradeLevel ?? '--'
      },
      {
        field: 'mode',
        headerName: 'Delivery mode',
        width: 150,
        renderCell: ({ row }) => formatDeliveryMode(row)
      },
      {
        field: 'phone',
        headerName: 'Phone',
        width: 150,
        renderCell: ({ row }) => row?.phone || '--'
      },
      {
        field: 'location',
        headerName: 'Location',
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }) => formatLocationSummary(row)
      },
      {
        field: 'subjects',
        headerName: 'Subjects',
        flex: 1.2,
        minWidth: 220,
        renderCell: ({ row }) => getSubjectNames(row)
      },
      {
        field: 'intake',
        headerName: 'Intake',
        minWidth: 180,
        flex: 1,
        renderCell: ({ row }) => row.matricEnrollment?.intakeLabel
          || [row.matricEnrollment?.examSession, row.matricEnrollment?.examYear].filter(Boolean).join(' ')
          || '--'
      },
      {
        field: 'payment',
        headerName: 'Registration / Tuition',
        minWidth: 225,
        flex: 1.1,
        renderCell: ({ row }) => {
          const enrollment = row.matricEnrollment;
          if (!enrollment) return '--';
          const registrationPaid = ['paid_gateway', 'paid_manual'].includes(enrollment.registrationFeeStatus);
          const tuitionPaid = ['paid_gateway', 'paid_manual'].includes(enrollment.paymentStatus);
          return `${registrationPaid ? 'Registration paid' : 'Registration pending'} / ${tuitionPaid ? 'Tuition paid' : 'Tuition pending'}`;
        }
      },
      {
        field: 'access',
        headerName: 'Lesson access',
        minWidth: 160,
        renderCell: ({ row }) => row.matricEnrollment?.accessStatus?.replace(/_/g, ' ') || '--'
      },
      {
        field: 'educators',
        headerName: 'Educators',
        flex: 1.2,
        minWidth: 200,
        renderCell: ({ row }) => getEducatorNames(row)
      },
      {
        field: 'status',
        headerName: 'LMS account',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => <StatusChip locked={Boolean(row.isLocked)} />
      },
      {
        field: 'curricula',
        headerName: 'Curriculum',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => row.matricEnrollment?.curriculum || formatLabelList(row?.curricula)
      },
      {
        field: 'profileSummary',
        headerName: 'Recorded Details',
        flex: 1.4,
        minWidth: 260,
        renderCell: ({ row }) => {
          const summary = formatProfileSummary(row);
          return summary === '--' ? (
            '--'
          ) : (
            <Tooltip title={summary}>
              <span>{summary}</span>
            </Tooltip>
          );
        }
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => formatDateTime(row?.createdAt)
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            <IconButton size="small" aria-label="View profile" onClick={() => setViewDialog({ open: true, user: row })}>
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="More actions" onClick={(event) => handleMenuOpen(event, 'learner', row)}>
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        )
      }
    ],
    [getEducatorNames, getSubjectNames]
  );

  const rows = activeTab === 'classes' ? filteredClasses : activeTab === 'educators' ? filteredEducators : filteredLearners;
  const columns = activeTab === 'classes' ? classColumns : activeTab === 'educators' ? educatorColumns : learnerColumns;
  const handleMenuAction = (action) => {
    const current = menuRow;
    closeMenu();
    if (!current) return;
    const { type, data } = current;
    if (type === 'class') {
      if (action === 'edit') openClassDialog('edit', data);
      if (action === 'delete') requestDeleteClass(data);
    } else if (type === 'educator') {
      if (action === 'view') setViewDialog({ open: true, user: data });
      if (action === 'edit') openEducatorDialog(data);
      if (action === 'reset-password') openResetPasswordDialog(data);
      if (action === 'lock') handleLockUser(data, 'lock', 'educator');
      if (action === 'unlock') handleLockUser(data, 'unlock', 'educator');
      if (action === 'delete') {
        requestDeleteUser(data._id, `${data.name || ''} ${data.surname || ''}`.trim() || data.email || 'this educator');
      }
    } else if (type === 'learner') {
      if (action === 'view') setViewDialog({ open: true, user: data });
      if (action === 'edit') openLearnerDialog(data);
      if (action === 'reset-password') openResetPasswordDialog(data);
      if (action === 'lock') handleLockUser(data, 'lock', 'learner');
      if (action === 'unlock') handleLockUser(data, 'unlock', 'learner');
      if (action === 'delete') {
        requestDeleteUser(data._id, `${data.name || ''} ${data.surname || ''}`.trim() || data.email || 'this learner');
      }
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={refreshAll}>
            Refresh
          </Button>
        </Stack>

        {loading && <LinearProgress />}

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {feedback && (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <TextField
              label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search classes, educators, or learners"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{ width: { xs: '100%', md: 320 } }}
            />

            <AdminActionGroup align="end">
              {activeTab === 'classes' && (
                <Button variant="contained" startIcon={<EventAvailableRoundedIcon />} onClick={() => openClassDialog('create')}>
                  Schedule Class
                </Button>
              )}
              {activeTab === 'educators' && (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openEducatorDialog()}>
                  Add Educator
                </Button>
              )}
              {activeTab === 'learners' && (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openLearnerDialog()}>
                  Add Learner
                </Button>
              )}
            </AdminActionGroup>
          </Stack>
        </Paper>


        <Paper sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="classes" label={`Classes (${filteredClasses.length})`} />
            <Tab value="educators" label={`Educators (${filteredEducators.length})`} />
            <Tab value="learners" label={`Learners (${filteredLearners.length})`} />
          </Tabs>
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row._id || row.id}
              disableRowSelectionOnClick
              loading={loading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
              density="comfortable"
              sx={{ border: 0, '& .MuiDataGrid-cell': { alignItems: 'center' } }}
              onRowDoubleClick={({ row }) => {
                if (activeTab === 'classes') return;
                setViewDialog({ open: true, user: row });
              }}
            />
          </Box>
        </Paper>
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuRow?.type === 'class' && (
          <>
            <MenuItem onClick={() => handleMenuAction('edit')}>
              <EditRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Edit class
            </MenuItem>
            <MenuItem onClick={() => handleMenuAction('delete')}>
              <DeleteRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Delete class
            </MenuItem>
          </>
        )}
        {(menuRow?.type === 'educator' || menuRow?.type === 'learner') && (
          <>
            <MenuItem onClick={() => handleMenuAction('view')}>
              <VisibilityRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              View profile
            </MenuItem>
            <MenuItem onClick={() => handleMenuAction('edit')}>
              <EditRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            <MenuItem onClick={() => handleMenuAction('reset-password')}>
              <PasswordRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Reset password
            </MenuItem>
            {(menuRow?.type === 'educator' || menuRow?.type === 'learner') &&
              (menuRow?.data?.isLocked ? (
                <MenuItem onClick={() => handleMenuAction('unlock')}>
                  <LockOpenRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                  Unlock account
                </MenuItem>
              ) : (
                <MenuItem onClick={() => handleMenuAction('lock')}>
                  <LockRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                  Lock account
                </MenuItem>
              ))}
            <MenuItem onClick={() => handleMenuAction('delete')}>
              <DeleteRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
      <UserProfileDialog
        open={viewDialog.open}
        onClose={() => setViewDialog({ open: false, user: null })}
        user={viewDialog.user}
        title="Matric Programme User Profile"
        getDisplayName={getDisplayName}
        getTutorNames={getEducatorNames}
        getSubjectNames={getSubjectNames}
      />
      <Dialog open={educatorDialog.open} onClose={educatorDialog.loading ? undefined : closeEducatorDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{educatorDialog.mode === 'edit' ? 'Edit Educator' : 'Add Educator'}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="educator-form" onSubmit={handleEducatorSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="First name"
                  value={educatorForm.name}
                  onChange={(event) => setEducatorForm((prev) => ({ ...prev, name: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Surname"
                  value={educatorForm.surname}
                  onChange={(event) => setEducatorForm((prev) => ({ ...prev, surname: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={educatorForm.email}
                  onChange={(event) => setEducatorForm((prev) => ({ ...prev, email: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              {educatorDialog.mode === 'create' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Temporary password"
                    value={educatorForm.password}
                    onChange={(event) => setEducatorForm((prev) => ({ ...prev, password: event.target.value }))}
                    helperText="Minimum 8 characters recommended."
                    fullWidth
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={educatorForm.phone}
                  onChange={(event) => setEducatorForm((prev) => ({ ...prev, phone: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Country"
                  value={educatorForm.country}
                  onChange={(event) => setEducatorForm((prev) => ({ ...prev, country: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Province"
                  value={educatorForm.province}
                  onChange={(event) => setEducatorForm((prev) => ({ ...prev, province: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Town / City"
                  value={educatorForm.town}
                  onChange={(event) => setEducatorForm((prev) => ({ ...prev, town: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Suburb"
                  value={educatorForm.suburb}
                  onChange={(event) => setEducatorForm((prev) => ({ ...prev, suburb: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="educator-curricula-label">Curricula</InputLabel>
                  <Select
                    labelId="educator-curricula-label"
                    multiple
                    label="Curricula"
                    value={educatorForm.curricula}
                    onChange={(event) => setEducatorForm((prev) => ({ ...prev, curricula: event.target.value }))}
                    input={<OutlinedInput label="Curricula" />}
                    renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                  >
                    {curriculumOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={educatorForm.curricula.includes(option)} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!educatorForm.tutoringOptions.length}>
                  <InputLabel id="educator-mode-label">Delivery mode</InputLabel>
                  <Select
                    labelId="educator-mode-label"
                    label="Delivery mode"
                    multiple
                    value={educatorForm.tutoringOptions}
                    onChange={(event) => setEducatorForm((prev) => ({ ...prev, tutoringOptions: event.target.value }))}
                    input={<OutlinedInput label="Delivery mode" />}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None';
                      return selected
                        .map((option) => option.replace(/\b\w/g, (c) => c.toUpperCase()))
                        .join(', ');
                    }}
                  >
                    <MenuItem value="online">
                      <Checkbox checked={educatorForm.tutoringOptions.includes('online')} />
                      <ListItemText primary="Online" />
                    </MenuItem>
                    <MenuItem value="in-person">
                      <Checkbox checked={educatorForm.tutoringOptions.includes('in-person')} />
                      <ListItemText primary="In-person" />
                    </MenuItem>
                  </Select>
                  {!educatorForm.tutoringOptions.length && <FormHelperText>Select at least one tutoring option</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!educatorForm.subjectIds.length}>
                  <InputLabel id="educator-subjects-label">Subjects</InputLabel>
                  <Select
                    labelId="educator-subjects-label"
                    multiple
                    value={educatorForm.subjectIds}
                    onChange={(event) => setEducatorForm((prev) => ({ ...prev, subjectIds: event.target.value }))}
                    input={<OutlinedInput label="Subjects" />}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None';
                      return selected
                        .map((id) => subjectMap.get(String(id))?.name)
                        .filter(Boolean)
                        .join(', ');
                    }}
                  >
                    {matricSubjects.map((subject) => (
                      <MenuItem key={subject._id} value={subject._id}>
                        <Checkbox checked={educatorForm.subjectIds.includes(subject._id)} />
                        <ListItemText primary={subject.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  {!educatorForm.subjectIds.length && <FormHelperText>Select at least one subject</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEducatorDialog} disabled={educatorDialog.loading}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form="educator-form" disabled={educatorDialog.loading}>
            {educatorDialog.loading ? 'Saving...' : educatorDialog.mode === 'edit' ? 'Save changes' : 'Add educator'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={learnerDialog.open} onClose={learnerDialog.loading ? undefined : closeLearnerDialog} maxWidth="md" fullWidth>
        <DialogTitle>{learnerDialog.mode === 'edit' ? 'Edit Learner' : 'Add Learner'}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="learner-form" onSubmit={handleLearnerSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="First name"
                  value={learnerForm.name}
                  onChange={(event) => setLearnerForm((prev) => ({ ...prev, name: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Surname"
                  value={learnerForm.surname}
                  onChange={(event) => setLearnerForm((prev) => ({ ...prev, surname: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Email"
                  type="email"
                  value={learnerForm.email}
                  onChange={(event) => setLearnerForm((prev) => ({ ...prev, email: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              {learnerDialog.mode === 'create' && (
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Temporary password"
                    value={learnerForm.password}
                    onChange={(event) => setLearnerForm((prev) => ({ ...prev, password: event.target.value }))}
                    helperText="Minimum 8 characters recommended."
                    fullWidth
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <TextField label="Grade" value="12" disabled fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Phone"
                  value={learnerForm.phone}
                  onChange={(event) => setLearnerForm((prev) => ({ ...prev, phone: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Country"
                  value={learnerForm.country}
                  onChange={(event) => setLearnerForm((prev) => ({ ...prev, country: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Province"
                  value={learnerForm.province}
                  onChange={(event) => setLearnerForm((prev) => ({ ...prev, province: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Town / City"
                  value={learnerForm.town}
                  onChange={(event) => setLearnerForm((prev) => ({ ...prev, town: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Suburb"
                  value={learnerForm.suburb}
                  onChange={(event) => setLearnerForm((prev) => ({ ...prev, suburb: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="learner-curricula-label">Curricula</InputLabel>
                  <Select
                    labelId="learner-curricula-label"
                    multiple
                    label="Curricula"
                    value={learnerForm.curricula}
                    onChange={(event) => setLearnerForm((prev) => ({ ...prev, curricula: event.target.value }))}
                    input={<OutlinedInput label="Curricula" />}
                    renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                  >
                    {curriculumOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={learnerForm.curricula.includes(option)} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!learnerForm.tutoringOptions.length}>
                  <InputLabel id="learner-mode-label">Delivery mode</InputLabel>
                  <Select
                    labelId="learner-mode-label"
                    label="Delivery mode"
                    multiple
                    value={learnerForm.tutoringOptions}
                    onChange={(event) => setLearnerForm((prev) => ({ ...prev, tutoringOptions: event.target.value }))}
                    input={<OutlinedInput label="Delivery mode" />}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None';
                      return selected
                        .map((option) => option.replace(/\b\w/g, (c) => c.toUpperCase()))
                        .join(', ');
                    }}
                  >
                    <MenuItem value="online">
                      <Checkbox checked={learnerForm.tutoringOptions.includes('online')} />
                      <ListItemText primary="Online" />
                    </MenuItem>
                    <MenuItem value="in-person">
                      <Checkbox checked={learnerForm.tutoringOptions.includes('in-person')} />
                      <ListItemText primary="In-person" />
                    </MenuItem>
                  </Select>
                  {!learnerForm.tutoringOptions.length && <FormHelperText>Select at least one tutoring option</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControl fullWidth required error={!learnerForm.subjectIds.length}>
                  <InputLabel id="learner-subjects-label">Subjects</InputLabel>
                  <Select
                    labelId="learner-subjects-label"
                    multiple
                    value={learnerForm.subjectIds}
                    onChange={(event) => setLearnerForm((prev) => ({ ...prev, subjectIds: event.target.value }))}
                    input={<OutlinedInput label="Subjects" />}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None';
                      return selected
                        .map((id) => subjectMap.get(String(id))?.name)
                        .filter(Boolean)
                        .join(', ');
                    }}
                  >
                    {matricSubjects.map((subject) => (
                      <MenuItem key={subject._id} value={subject._id}>
                        <Checkbox checked={learnerForm.subjectIds.includes(subject._id)} />
                        <ListItemText primary={subject.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  {!learnerForm.subjectIds.length && <FormHelperText>Select at least one subject</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="learner-educator-label">Educators</InputLabel>
                  <Select
                    labelId="learner-educator-label"
                    multiple
                    value={learnerForm.tutorIds}
                    onChange={(event) => setLearnerForm((prev) => ({ ...prev, tutorIds: event.target.value }))}
                    input={<OutlinedInput label="Educators" />}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None';
                      return selected
                        .map((id) => {
                          const tutor = educatorMap.get(String(id));
                          if (!tutor) return null;
                          return `${tutor.name || ''} ${tutor.surname || ''}`.trim();
                        })
                        .filter(Boolean)
                        .join(', ');
                    }}
                  >
                    {educators.map((tutor) => (
                      <MenuItem key={tutor._id} value={tutor._id}>
                        <Checkbox checked={learnerForm.tutorIds.includes(tutor._id)} />
                        <ListItemText primary={`${tutor.name || ''} ${tutor.surname || ''}`.trim()} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLearnerDialog} disabled={learnerDialog.loading}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form="learner-form" disabled={learnerDialog.loading}>
            {learnerDialog.loading ? 'Saving...' : learnerDialog.mode === 'edit' ? 'Save changes' : 'Add learner'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={classDialog.open} onClose={classDialog.loading ? undefined : closeClassDialog} maxWidth="md" fullWidth>
        <DialogTitle>{classDialog.mode === 'edit' ? 'Edit Class' : 'Schedule Class'}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="class-form" onSubmit={handleClassSave} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="class-subject-label">Subject</InputLabel>
                  <Select
                    labelId="class-subject-label"
                    label="Subject"
                    value={classForm.subject}
                    onChange={(event) => setClassForm((prev) => ({ ...prev, subject: event.target.value }))}
                  >
                    <MenuItem value="">
                      <em>Select subject</em>
                    </MenuItem>
                    {classSubjectOptions.map((subject) => (
                      <MenuItem key={subject} value={subject}>
                        {subject}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Grade" value="12" disabled fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Start time"
                  type="datetime-local"
                  value={classForm.startTime}
                  onChange={(event) => setClassForm((prev) => ({ ...prev, startTime: event.target.value }))}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="End time"
                  type="datetime-local"
                  value={classForm.endTime}
                  onChange={(event) => setClassForm((prev) => ({ ...prev, endTime: event.target.value }))}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!classForm.tutorId}>
                  <InputLabel id="class-educator-label">Educator</InputLabel>
                  <Select
                    labelId="class-educator-label"
                    label="Educator"
                    value={classForm.tutorId}
                    onChange={(event) => setClassForm((prev) => ({ ...prev, tutorId: event.target.value }))}
                  >
                    <MenuItem value="">
                      <em>Select educator</em>
                    </MenuItem>
                    {educators.map((tutor) => (
                      <MenuItem key={tutor._id} value={tutor._id}>
                        {`${tutor.name || ''} ${tutor.surname || ''}`.trim() || tutor.email || 'Educator'}
                      </MenuItem>
                    ))}
                  </Select>
                  {!classForm.tutorId && <FormHelperText>Select an educator</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Schedule label"
                  value={classForm.scheduleLabel}
                  onChange={(event) => setClassForm((prev) => ({ ...prev, scheduleLabel: event.target.value }))}
                  fullWidth
                  placeholder="e.g., Wed 19:00"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="class-learners-label">Learners</InputLabel>
                  <Select
                    labelId="class-learners-label"
                    multiple
                    value={classForm.studentIds}
                    onChange={(event) => setClassForm((prev) => ({ ...prev, studentIds: event.target.value }))}
                    input={<OutlinedInput label="Learners" />}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None';
                      return selected
                        .map((id) => {
                          const learner = learnerMap.get(String(id));
                          if (!learner) return null;
                          return `${learner.name || ''} ${learner.surname || ''}`.trim();
                        })
                        .filter(Boolean)
                        .join(', ');
                    }}
                  >
                    {learners.map((learner) => (
                      <MenuItem key={learner._id} value={learner._id}>
                        <Checkbox checked={classForm.studentIds.includes(learner._id)} />
                        <ListItemText primary={`${learner.name || ''} ${learner.surname || ''}`.trim()} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClassDialog} disabled={classDialog.loading}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form="class-form" disabled={classDialog.loading}>
            {classDialog.loading ? 'Saving...' : classDialog.mode === 'edit' ? 'Save changes' : 'Create class'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDialog.open} onClose={confirmDialog.loading ? undefined : () => setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null })}
            disabled={confirmDialog.loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (confirmDialog.action) {
                await confirmDialog.action();
              }
            }}
            disabled={confirmDialog.loading}
          >
            {confirmDialog.loading ? 'Working...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={passwordDialog.open}
        onClose={
          passwordDialog.loading
            ? undefined
            : () => setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              User: {passwordDialog.user ? `${passwordDialog.user.name || ''} ${passwordDialog.user.surname || ''}`.trim() || passwordDialog.user.email : ''}
            </Typography>
            <TextField
              label="New password"
              type="password"
              value={passwordDialog.newPassword}
              onChange={(event) => setPasswordDialog((prev) => ({ ...prev, newPassword: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Confirm password"
              type="password"
              value={passwordDialog.confirmPassword}
              onChange={(event) => setPasswordDialog((prev) => ({ ...prev, confirmPassword: event.target.value }))}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' })}
            disabled={passwordDialog.loading}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={passwordDialog.loading}>
            {passwordDialog.loading ? 'Resetting...' : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default MatricClasses;
